// =====================================================
// Admin · Observabilidad de IA
// Tabla y KPIs de cada llamada a la IA: estado, modelo, tokens,
// tiempo y coste real en USD. Solo accesible para administradores
// (RLS deja pasar solo a admins; ocultamos la UI por comodidad).
// =====================================================
import React from 'react';
import { Icon } from '../icons.jsx';
import { supabase } from '../integrations/supabase/client';
import { useAuth } from '../auth/AuthContext.jsx';

const { useState, useEffect, useCallback, useMemo } = React;

const FN_LABEL = {
  'bid-analyze': 'Análisis',
  'bid-analyze:section': 'Análisis',
  'bid-analyze:gemini-digest': 'Análisis',
  'bid-chat': 'Chat',
  'extract-document': 'Extracción',
};

// Etiquetas legibles para cada sección del pipeline de análisis.
const SECTION_LABEL = {
  'gemini-digest': 'Ingestión',
  decision: 'Decisión y base',
  economic: 'Económico',
  criteria: 'Criterios',
  capacity: 'Solvencia/Equipo/Técnico',
  risk: 'Riesgos/ANS',
  docs: 'Documentación',
  report: 'Informe ejecutivo',
};

const RANGES = [
  { id: '24h', label: '24 h', ms: 24 * 3600e3 },
  { id: '7d', label: '7 días', ms: 7 * 24 * 3600e3 },
  { id: '30d', label: '30 días', ms: 30 * 24 * 3600e3 },
  { id: 'all', label: 'Todo', ms: null },
];

function usd(n) {
  const v = Number(n) || 0;
  if (v > 0 && v < 0.01) return '$' + v.toFixed(4);
  return '$' + v.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function num(n) { return (Number(n) || 0).toLocaleString('es-ES'); }
function ms(n) {
  const v = Number(n) || 0;
  if (v < 1000) return v + ' ms';
  return (v / 1000).toFixed(1) + ' s';
}
function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch (e) { return iso; }
}

function Kpi({ icon, label, value, sub, tone }) {
  const Ico = Icon[icon];
  return (
    <div className="card card-pad" style={{ flex: 1, minWidth: 160 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span className={`sem ${tone || 'sem-neutral'}`} style={{ width: 30, height: 30, borderRadius: 9, justifyContent: 'center', padding: 0 }}>
          {Ico && <Ico size={15} />}
        </span>
        <span style={{ fontSize: 11.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      </div>
      <div className="font-display-heavy" style={{ fontSize: 26, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

export function AdminLogsScreen() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('7d');
  const [fn, setFn] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // all | error | retry
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    let q = supabase.from('ai_usage_logs').select('*').order('created_at', { ascending: false }).limit(1000);
    const r = RANGES.find((x) => x.id === range);
    if (r?.ms) q = q.gte('created_at', new Date(Date.now() - r.ms).toISOString());
    if (fn !== 'all') q = q.eq('function', fn);
    if (statusFilter !== 'all') q = q.eq('status', statusFilter);
    const { data, error } = await q;
    if (error) console.error('[admin-logs]', error.message);
    setRows(data || []);
    setLoading(false);
  }, [range, fn, statusFilter]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const stats = useMemo(() => {
    const s = { cost: 0, inT: 0, outT: 0, calls: rows.length, errors: 0, retries: 0, dur: 0 };
    for (const r of rows) {
      s.cost += Number(r.cost_usd) || 0;
      s.inT += r.input_tokens || 0;
      s.outT += r.output_tokens || 0;
      s.dur += r.duration_ms || 0;
      if (r.status === 'error') s.errors += 1;
      else if (r.status === 'retry') s.retries += 1;
    }
    s.avgDur = rows.length ? Math.round(s.dur / rows.length) : 0;
    s.errPct = rows.length ? Math.round((s.errors / rows.length) * 100) : 0;
    return s;
  }, [rows]);

  if (!isAdmin) {
    return (
      <div className="viewport-inner fade-in">
        <div className="page-head"><div><div className="page-eyebrow">Acceso restringido</div><h1 className="page-title">Logs de IA</h1></div></div>
        <div className="card card-pad" style={{ padding: 60, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: 'var(--bg-2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon.Shield size={26} style={{ color: 'var(--text-3)' }} />
          </div>
          <div className="font-display-heavy" style={{ fontSize: 22 }}>Solo para administradores</div>
          <p style={{ fontSize: 14, color: 'var(--text-3)', maxWidth: 460 }}>El registro de uso y coste de la IA está reservado a administradores.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="viewport-inner fade-in">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Observabilidad · {loading ? 'cargando…' : `${stats.calls} llamadas`}</div>
          <h1 className="page-title">Logs de IA</h1>
          <p className="muted" style={{ fontSize: 14, marginTop: 2, maxWidth: 620 }}>
            Consumo de tokens, tiempo de respuesta, coste real en USD y errores de cada llamada a la IA.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline" onClick={load} disabled={loading}>
            <Icon.Activity size={14} /> Actualizar
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <Kpi icon="Coins" label="Coste total" value={usd(stats.cost)} tone="sem-success" sub={`${num(stats.inT + stats.outT)} tokens`} />
        <Kpi icon="Cpu" label="Tokens" value={num(stats.inT + stats.outT)} tone="sem-info" sub={`${num(stats.inT)} in · ${num(stats.outT)} out`} />
        <Kpi icon="Activity" label="Llamadas" value={num(stats.calls)} tone="sem-violet" />
        <Kpi icon="AlertTriangle" label="Errores" value={`${stats.errors}`} tone={stats.errors ? 'sem-danger' : 'sem-neutral'} sub={`${stats.errPct}% del total`} />
        <Kpi icon="Refresh" label="Reintentos" value={`${stats.retries}`} tone={stats.retries ? 'sem-warning' : 'sem-neutral'} sub="recuperados" />
        <Kpi icon="Clock" label="Tiempo medio" value={ms(stats.avgDur)} tone="sem-warning" />
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        <div className="tabs" style={{ border: 'none' }}>
          {RANGES.map((r) => <div key={r.id} className={`tab ${range === r.id ? 'active' : ''}`} onClick={() => setRange(r.id)}>{r.label}</div>)}
        </div>
        <div style={{ flex: 1 }} />
        <select className="input" style={{ width: 'auto', fontSize: 12.5 }} value={fn} onChange={(e) => setFn(e.target.value)}>
          <option value="all">Todas las funciones</option>
          <option value="bid-analyze">Análisis</option>
          <option value="bid-chat">Chat</option>
          <option value="extract-document">Extracción</option>
        </select>
        <button className={`btn ${statusFilter === 'error' ? 'btn-primary' : 'btn-outline'}`} style={{ fontSize: 12.5 }} onClick={() => setStatusFilter((v) => v === 'error' ? 'all' : 'error')}>
          <Icon.AlertTriangle size={13} /> Solo errores
        </button>
        <button className={`btn ${statusFilter === 'retry' ? 'btn-primary' : 'btn-outline'}`} style={{ fontSize: 12.5 }} onClick={() => setStatusFilter((v) => v === 'retry' ? 'all' : 'retry')}>
          <Icon.Refresh size={13} /> Solo reintentos
        </button>
      </div>

      {/* Tabla */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Fecha</th><th>Función</th><th>Sección</th><th>Modelo</th><th>Estado</th>
              <th style={{ textAlign: 'right' }}>Tokens in</th>
              <th style={{ textAlign: 'right' }}>Tokens out</th>
              <th style={{ textAlign: 'right' }}>Tiempo</th>
              <th style={{ textAlign: 'right' }}>Coste</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} style={{ padding: 28, textAlign: 'center', color: 'var(--text-3)' }}>Cargando…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={9} style={{ padding: 28, textAlign: 'center', color: 'var(--text-3)' }}>Sin registros en este periodo.</td></tr>}
            {!loading && rows.map((r) => {
              const err = r.status === 'error';
              const retry = r.status === 'retry';
              const expandable = err || retry;
              const isOpen = expanded === r.id;
              const sectionLabel = r.section
                ? (SECTION_LABEL[r.section] || r.section) + (r.attempt ? ` · intento ${r.attempt}` : '')
                : '—';
              return (
                <React.Fragment key={r.id}>
                  <tr style={{ cursor: expandable ? 'pointer' : 'default' }} onClick={() => expandable && setExpanded(isOpen ? null : r.id)}>
                    <td style={{ fontSize: 12.5, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</td>
                    <td style={{ fontSize: 12.5 }}>{FN_LABEL[r.function] || r.function}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-3)' }}>{sectionLabel}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-3)', fontFamily: 'var(--font-mono, monospace)' }}>{r.model || '—'}</td>
                    <td>
                      <span className={`sem ${err ? 'sem-danger' : retry ? 'sem-warning' : 'sem-success'}`} style={{ fontSize: 10.5, padding: '2px 8px' }}>
                        {err ? 'Error' : retry ? 'Reintento' : 'OK'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', fontSize: 12.5, color: 'var(--text-2)' }}>{num(r.input_tokens)}</td>
                    <td style={{ textAlign: 'right', fontSize: 12.5, color: 'var(--text-2)' }}>{num(r.output_tokens)}</td>
                    <td style={{ textAlign: 'right', fontSize: 12.5, color: 'var(--text-2)' }}>{ms(r.duration_ms)}</td>
                    <td style={{ textAlign: 'right', fontSize: 12.5, fontWeight: 600 }}>{usd(r.cost_usd)}</td>
                  </tr>
                  {expandable && isOpen && (
                    <tr>
                      <td colSpan={9} style={{ background: 'var(--bg-1)', padding: '10px 14px' }}>
                        <div style={{ fontSize: 11.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{retry ? 'Motivo del reintento' : 'Mensaje de error'}</div>
                        <div style={{ fontSize: 12.5, color: retry ? 'var(--sem-warning, #f5a623)' : 'var(--sem-danger, #e5484d)', fontFamily: 'var(--font-mono, monospace)', whiteSpace: 'pre-wrap' }}>{r.error || 'Sin detalle'}</div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {!loading && rows.length >= 1000 && (
        <div style={{ fontSize: 11.5, color: 'var(--text-4)', marginTop: 10 }}>Mostrando los 1000 registros más recientes. Acota el rango para ver más.</div>
      )}
    </div>
  );
}
